import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuInput } from "../../../components/neu/Neu";
import { DateInput } from "../../../components/neu/DateInput";
import { HolidayRow } from "./HolidayRow";
import { createHoliday, deleteHoliday, listHolidays } from "../../../api/directory";

const EMPTY = { name: "", name_ar: "", start_date: "", end_date: "", paid: true, recurring_yearly: false };

export const HolidaysTab = ({ form, set }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const excluded = form.excluded_holiday_ids || [];

  const load = async () => setItems(await listHolidays());
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!draft.name || !draft.start_date) return toast.error(t("holidays.missing"));
    try {
      await createHoliday({ ...draft, end_date: draft.end_date || draft.start_date });
      setDraft(EMPTY);
      toast.success(t("holidays.added"));
      load();
    } catch {
      toast.error(t("errors.generic"));
    }
  };

  const remove = async (id) => {
    await deleteHoliday(id);
    load();
  };

  const toggle = (id) =>
    set("excluded_holiday_ids", excluded.includes(id) ? excluded.filter((x) => x !== id) : [...excluded, id]);

  return (
    <div className="grid gap-5">
      <p className="text-xs text-muted-foreground">{t("holidays.note")}</p>

      <div className="grid gap-2 sm:grid-cols-5" data-testid="holiday-draft">
        <NeuInput data-testid="holiday-name" placeholder={t("holidays.name")} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <NeuInput data-testid="holiday-name-ar" placeholder={t("holidays.nameAr")} value={draft.name_ar} onChange={(e) => setDraft({ ...draft, name_ar: e.target.value })} />
        <DateInput testId="holiday-start" value={draft.start_date} onChange={(v) => setDraft({ ...draft, start_date: v })} />
        <DateInput testId="holiday-end" value={draft.end_date} onChange={(v) => setDraft({ ...draft, end_date: v })} />
        <NeuButton type="button" variant="primary" className="flex items-center justify-center gap-2" data-testid="holiday-add" onClick={add}>
          <CalendarPlus className="h-4 w-4" /> {t("holidays.add")}
        </NeuButton>
      </div>

      <ul className="divide-y divide-border/50" data-testid="holiday-list">
        {items.map((h) => (
          <HolidayRow
            key={h.id}
            item={h}
            excluded={excluded.includes(h.id)}
            onToggle={() => toggle(h.id)}
            onDelete={() => remove(h.id)}
            reload={load}
          />
        ))}
      </ul>
    </div>
  );
};
